import { Component, OnInit, ViewChild, Input, Output, EventEmitter, AfterViewInit } from '@angular/core';
import { DoctorInformationService } from './services/doctor-information.service';
import { MatAutocompleteTrigger } from '@angular/material/autocomplete';
import { StateStructure } from '@ptl/state';
import {
  addDoctor,
  removeDoctors,
  addPCP,
  DoctorStateSapphireDigital,
  removePCP,
  updateDoctors,
  updatePCP,
  DOCTOR_STATE_REDUX_ID_SAPPHIRE_DIGITAL
} from './configs/reducers/doctors.reducer';
import { GoogleMapApiService, LatLng } from 'src/app/shared/services/google-map-api.service';
import { NgRedux } from '@ptl/angular-redux-store';
import { ReducerUtilityService } from 'src/app/shared/utility-services/reducer-utility.service';
import { ProviderLookupAutoCompleteResponse } from './configs/interfaces/provider-lookup/response/response.interface';
import { UntypedFormBuilder, Form, UntypedFormGroup, AbstractControl } from '@angular/forms';
import { ZipCountyStateStructure } from 'src/app/angular-elements/shared/interfaces/zipCountyStateStructure.interface';
import { Doctor, OtherLocationsResponse } from './configs/interfaces/doctors.interface';
import { HttpErrorResponse } from '@angular/common/http';
import { PtlWindowLocationService } from '@ptl/global-components';
import { environment } from 'src/environments/environment';
import { debounceTime, tap } from 'rxjs/operators';
import { LINE_OF_BUSINESS_ENUM } from '@ptl/account-management';

export interface DoctorInformationFormAsJson {
  radius: number;
  zipCode: string;
  searchString: string;
}

export interface DoctorInformationFormControls {
  radius: AbstractControl;
  zipCode: AbstractControl;
  searchString: AbstractControl;
}


export interface DoctorInformationFormAsJson {
  radius: number;
  zipCode: string;
  searchString: string;
}

export class HttpError {
  static InternalServerError = 500;
}

@Component({
  selector: 'app-doctor-information-sapphire-digital',
  templateUrl: './doctor-information.component.html',
  styleUrls: ['./doctor-information.component.scss'],
  standalone: false
})
export class DoctorInformationComponent implements OnInit {
  @Input() title = 'Find Doctors';
  @Input() subTitle;
  @Input() description;
  @Input() primaryCareProviderOnly = false;
  @Input() nextButtonLabel: string;
  @Input() nextButtonUrl: string;
  @Input() backButtonLabel: string;
  @Input() backButtonUrl: string;
  @Input() market: string;
  @Input() defaultRadius: string;

  // If set to true will include branded network id in provider search
  @Input() useBrandNetwork: string;
  useBrandNetworkBool: boolean = false;

  @Output() doctorsChanged = new EventEmitter<Doctor[]>();

  @ViewChild(MatAutocompleteTrigger, { read: MatAutocompleteTrigger, static: true })
  inputAutoComplit: MatAutocompleteTrigger;

  disableSubmit: boolean;
  showSpinner: boolean;
  broadSearch: boolean;
  minimumCharError: boolean = false;
  additionalInfoLoading: boolean = false;

  radius = [
    { id: 5, value: '5 miles' },
    { id: 10, value: '10 miles' },
    { id: 25, value: '25 miles' },
    { id: 50, value: '50 miles' },
    { id: 100, value: '100 miles' }
  ];
  autoCompleteResults = [];
  otherLocations = [];

  form: UntypedFormGroup;
  lastZipCodeQueried: string;
  lastLatLngResponse: LatLng;
  angularHostUrl: string;

  constructor(
    private fb: UntypedFormBuilder,
    private doctorService: DoctorInformationService,
    private ngRedux: NgRedux<StateStructure>,
    private googleMapApiService: GoogleMapApiService,
    private reducerUtilityService: ReducerUtilityService,
    private windowLocationService: PtlWindowLocationService
  ) {
  }

  get formControls(): DoctorInformationFormControls {
    return this.form as unknown as DoctorInformationFormControls;
  }

  get formValuesAsJson(): DoctorInformationFormAsJson {
    return this.form.value as unknown as DoctorInformationFormAsJson;
  }

  selectedDoctors: Doctor[] = [];
  showNoResults: Boolean = false;

  ngOnInit() {
    const zipCountyState: ZipCountyStateStructure = this.reducerUtilityService.getState('ZipCounty');

    this.form = this.fb.group({
      radius: this.fb.control(this.defaultRadius ? +this.defaultRadius : 10),
      zipCode: this.fb.control(zipCountyState.zipCode),
      searchString: this.fb.control(''),
    });

    this.angularHostUrl = environment.infrastructure.googleCloud.angularHostUrl;
    this.showSpinner = false;
    this.useBrandNetworkBool = this.useBrandNetwork === 'true' ? true : false;

    // Update title for PCP flow
    if (this.primaryCareProviderOnly) {
      this.title = "Primary Care Provider Information";
    }

    // Broadcast doctors on timeout to update form controls that appear later on the page
    // TODO - implement some sort of 'pageFinishedLoading' event that we can subscribe to after all controls are added
    setTimeout(() => {
      this.doctorsChanged.emit(this.selectedDoctors);
    }, 500);

    const selectedDoctorsState: DoctorStateSapphireDigital = this.reducerUtilityService.getState(DOCTOR_STATE_REDUX_ID_SAPPHIRE_DIGITAL);

    if (this.primaryCareProviderOnly) {
      /** Pre-populate PCP from state during ENROLLMENT flow */
      if (selectedDoctorsState.pcp) {
        this.selectedDoctors.push(selectedDoctorsState.pcp);
      }
    }
    else {
      /** Pre-populated DOCTORS from state during ADD PROVIDER(S) page, DEMOGRAPHICS flow */
      Array.prototype.push.apply(this.selectedDoctors, selectedDoctorsState.doctors);
    }

    this.form.valueChanges.pipe(
      debounceTime(500),
      tap((searchValues: DoctorInformationFormAsJson) => {
        if (searchValues.searchString.length > 2) {
          this.minimumCharError = false;
          this.filterDoctorsAutoComplete();
        } else {
          this.inputAutoComplit.closePanel();
          this.minimumCharError = true;
        }
      })
    ).subscribe()
  }

  async doctorAdded(addedDoctor: Doctor) {
    this.showSpinner = true;
    this.additionalInfoLoading = true;
    addedDoctor.isSelected = true;

    if (this.primaryCareProviderOnly) {
      await this.addPCP(addedDoctor);
    }
    else {
      await this.addDoctor(addedDoctor);
    }

    this.showSpinner = false;
    this.additionalInfoLoading = false;
    this.doctorsChanged.emit(this.selectedDoctors);
  }

  /**
   * Add DOCTOR to selectedDoctors[] to be included as part of DEMOGRAPHICS flow
   * This is our NON-ENROLLMENT, flow
   * @param addedDoctor DOCTOR to be ADDED to SELECTEDDOCTORS[]
   */
  async addDoctor(addedDoctor: Doctor): Promise<void> {
    /** Get accepted networks */
    await this.getAcceptedNetworks(addedDoctor);

    // call summary API to get main location
    this.updateAddedDoctor(addedDoctor);
    // add the doctor to the list of doctors
    this.selectedDoctors.push(addedDoctor);

    this.ngRedux.dispatch(addDoctor(addedDoctor));
  }

  async getAcceptedNetworks(doctor: Doctor) {
    try {
      /** Add data to added doctor */
      const acceptedNetworksForDoctor = await this.doctorService.getAcceptedNetwork(doctor.providerId, doctor.locationId);
      doctor.networksAccepted = acceptedNetworksForDoctor.networks_accepted;
    } 
    catch (e) {
      console.error('Failed to retrieve accepted networks for ', doctor);
    }
  }

  /**
   * Set PCP as DOCTOR to be included as part of DEMOGRAPHICS flow
   * This is our NON-ENROLLMENT, flow
   * @param addedDoctor DOCTOR to be SET to PCP
   */
  async addPCP(addedDoctor: Doctor): Promise<void> {
    await this.getAcceptedNetworks(addedDoctor);

    // reset replaced doctor's selected flag back to false. if it's still in autocomplete results it will still be shown as 'added'
    // because autoCompleteResults can reset all references
    this.autoCompleteResults.filter((result) => addedDoctor.providerId !== result.providerId).forEach((doc) => doc.isSelected = false);

    // call summary API to get main location
    this.updateAddedDoctor(addedDoctor);
    
    // wipe out existing selected list of doctors
    this.selectedDoctors = [];
    // add the doctor to the list of doctors
    this.selectedDoctors.push(addedDoctor);
    
    this.ngRedux.dispatch(addPCP(addedDoctor));
  }

  async updateAddedDoctor(addedDoctor: Doctor) {
    try {
      const addedDoctorDetails = await this.doctorService.getProviderLocation(addedDoctor.providerId, addedDoctor.locationId);
      addedDoctor.address = `${addedDoctorDetails.addr_line1}, ${addedDoctorDetails.city}, ${addedDoctorDetails.state} `;

      const fivePartKey_providerId = await this.getIdentifiers(addedDoctor);
      addedDoctor.fivePartKey_providerId = fivePartKey_providerId;
      
      addedDoctor.title = addedDoctorDetails.specialty_names.join('<br/>');

      this.otherLocations = [];
      if (addedDoctor.locationsCount > 1) {
        this.getOtherLocations(addedDoctor);
      }

      this.inputAutoComplit.closePanel();
    }
    catch (e) {
      console.error('Failed to retrieve Provider summary for ', addedDoctor);
    }
  }

  async updateLocation(doctor: Doctor, location): Promise<void> {
    doctor.locationId = location.locationId;
    doctor.address = location.address;
    await this.getAcceptedNetworks(doctor);

    const fivePartKey_providerId = await this.getIdentifiers(doctor);
    doctor.fivePartKey_providerId = fivePartKey_providerId;

    if (this.primaryCareProviderOnly) {
      this.ngRedux.dispatch(updatePCP(doctor));
    }
    else {
      this.ngRedux.dispatch(updateDoctors(doctor));
    }
  }

  /**
   * remove doc from selectedDoctors[] to be removed as an item in the demographics flow
   * @param doc doctor to be removed from selectedDoctors[]
   */
  removeDoctor(doc: Doctor): void {
    // remove the doctor from list of selected ones
    const doctorIndex = this.selectedDoctors.indexOf(doc);
    this.selectedDoctors.splice(doctorIndex, 1);

    // reset selected object back to false. if it's still in autocomplete results it will reactivate
    // we can't just use the 'doc' argument because autoCompleteResults can reset all references
    const docResult = this.autoCompleteResults.filter((result) => doc.providerId === result.providerId);
    if (docResult[0]) {
      docResult[0].isSelected = false;
    }

    this.otherLocations = [];
    this.ngRedux.dispatch(removeDoctors(doc));
    this.ngRedux.dispatch(removePCP(doc));
    this.doctorsChanged.emit(this.selectedDoctors);
  }

  async filterDoctorsAutoComplete(): Promise<any> {
    this.broadSearch = false;
    this.showNoResults = false;
    this.showSpinner = true;
    this.disableSubmit = true;
    this.autoCompleteResults = [];

    const formValues: DoctorInformationFormAsJson = this.formValuesAsJson;

    try {
      /** Get Latitude/Longitude info */
      let latLng: LatLng;
      if (this.lastZipCodeQueried !== formValues.zipCode) {
        latLng = await this.getLatLng();
        this.lastLatLngResponse = latLng;
        this.lastZipCodeQueried = formValues.zipCode;
      } else {
        latLng = this.lastLatLngResponse;
      }

      /** Request Doctor Info */
      const response: ProviderLookupAutoCompleteResponse = await this.doctorService.byDocNameAutoComplete(
        this.market,
        formValues.searchString, // this.inputSearch,
        formValues.radius, // this.selectedRadius,
        latLng.lat,
        latLng.lng,
        this.primaryCareProviderOnly,
        this.useBrandNetworkBool
      );

      const arrayDoctors = response.providers;
      for (const doctor of arrayDoctors) {

        /** Only add, to auto-complete results, doctors who haven't been added */
        const isDoctorAlreadySelected
          = this.selectedDoctors.filter(doc => {
            return doc.providerId === doctor.provider_id;
          }).length > 0;

        this.autoCompleteResults.push({
          id: doctor.id,
          title: doctor.specialty,
          name: doctor.name,
          address:
            `${doctor.locations[0].city}, ` +
            `${doctor.locations[0].state}`,
          providerId: doctor.provider_id,
          locationId: doctor.locations[0].id,
          locationsCount: doctor.locations_count,
          isSelected: isDoctorAlreadySelected
        });
      }

      this.inputAutoComplit.openPanel();

      // After searching once, we can show the no results message (if applicable)
      this.showNoResults = true;

    } catch (e) {
      if (e instanceof HttpErrorResponse) {
        switch (e.status) {
          case HttpError.InternalServerError:
            this.broadSearch = true;
            break;
        }
      }
      console.error(e);
    }
    this.showSpinner = false;
    this.disableSubmit = false;
  }

  async getIdentifiers(provider: Doctor): Promise<string> {

    const formValues: DoctorInformationFormAsJson = this.formValuesAsJson;

    try {
      /** Get Latitude/Longitude info */
      let latLng: LatLng;
      if (this.lastZipCodeQueried !== formValues.zipCode) {
        latLng = await this.getLatLng();
        this.lastLatLngResponse = latLng;
        this.lastZipCodeQueried = formValues.zipCode;
      } else {
        latLng = this.lastLatLngResponse;
      }

      const response =
        await this.doctorService.getIdentifiers(
          provider.providerId,
          provider.locationId,
          latLng.lat,
          latLng.lng,
          this.primaryCareProviderOnly
        );
      const identifiers = response.identifiers;

      let fiveKeyPart = '';
      identifiers.forEach(identifier => {
        if(identifier.type_code === 'POR') {
          fiveKeyPart = identifier.value;
        }
      })
      return fiveKeyPart;
    } catch (e) {
      console.error('Failed to get Attributes', e, provider);
    }
  }

  async getOtherLocations(provider: Doctor) {
    try {
      const providerOtherLocations: OtherLocationsResponse =
        await this.doctorService.getOtherLocations(provider.providerId, provider.locationId);
      this.otherLocations = [];
      /** Add data of other locations */
      for (const doctor of providerOtherLocations.other_provider_locations) {
        /** Only add, to otherLocation results (doctor with locations), with different locationId's */
        const selectedDoctorAlreadyExists
          = this.selectedDoctors.filter(doc => {
            return doc.locationId === doctor.location_id;
          }).length > 0;

        if (!selectedDoctorAlreadyExists) {
          this.otherLocations.push({
            id: doctor.specialties[0].id,
            title: doctor.specialties[0].name,
            name: provider.name,
            address:
              `${doctor.addr_line1}, ` +
              `${doctor.city}, ` +
              `${doctor.state}`,
            providerId: doctor.provider_id,
            locationId: doctor.location_id
          });
        }
      }

      const currentLocation = {
        id: provider.id,
        title: provider.title,
        name: provider.name,
        address: provider.address,
        providerId: provider.providerId,
        locationId: provider.locationId
      };
      this.otherLocations.push(currentLocation);
      const providerIndex = this.selectedDoctors.indexOf(provider);
      if (this.selectedDoctors[providerIndex]) {
        this.selectedDoctors[providerIndex].otherLocations = this.otherLocations;
      }
    } catch (e) {
      console.error('Failed to get other locations', e, provider);
    }
  }
  /** get the zip code value entered by user */

  async getLatLng(): Promise<{ lat: number; lng: number }> {
    const formValues: DoctorInformationFormAsJson = this.formValuesAsJson;

    try {
      const googleMapsLatLngLookupResponse: LatLng
        = await this.googleMapApiService.getLatLngFromFormattedZipCode(formValues.zipCode).toPromise();

      return {
        lat: googleMapsLatLngLookupResponse.lat,
        lng: googleMapsLatLngLookupResponse.lng
      };
    } catch (e) {
      console.error('Failed to retrieved LAT and LNG from gmaps');
      return e;
    }
  }

  navBack() {
    if (this.backButtonUrl) {
      // window.location.href = `./${this.backButtonUrl}`;
      const finalUrl = `${this.backButtonUrl}`;
      this.windowLocationService.goToUrl(finalUrl);
    } else {
      console.error('Missing AEM url for {backButtonUrl}');
    }
  }

  navForward() {
    if (this.nextButtonUrl) {
      // window.location.href = `./${this.nextButtonUrl}`;
      const finalUrl = `${this.nextButtonUrl}`;
      this.windowLocationService.goToUrl(finalUrl);
    } else {
      console.error('Missing AEM url for {nextButtonUrl}');
    }
  }
}
